import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertCircle, Info, Key } from '@/lib/icons';
import { adminService, type AdminUser, type AdminUpdateData } from '@/services/admin-service';
import { affiliateService } from '@/services/affiliate-service';
import {
  Input,
  Label,
  Field,
  FieldError,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Checkbox,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Alert,
  AlertDescription,
  Switch,
  toast,
} from 'prosci-ui';

interface AdminUserEditDialogProps {
  user: AdminUser;
  onClose: () => void;
  onSuccess: () => void;
  canEditSuper?: boolean;
}

export function AdminUserEditDialog({ user, onClose, onSuccess, canEditSuper = false }: AdminUserEditDialogProps) {
  const [formData, setFormData] = useState({
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
    role: user.role,
    status: user.status,
    // Ensure affiliate_id is a string for Select component compatibility
    affiliate_id: user.affiliate_id ? String(user.affiliate_id) : '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sendPasswordReset, setSendPasswordReset] = useState(false);

  // Fetch affiliates when role is affiliate or affiliate_admin
  const { data: affiliatesData } = useQuery({
    queryKey: ['affiliates'],
    queryFn: () => affiliateService.listAffiliates(),
    enabled: formData.role === 'affiliate' || formData.role === 'affiliate_admin',
  });

  const affiliates = affiliatesData?.data?.map(item => ({
    ...item.attributes,
    id: item.id
  })) || [];

  const updateMutation = useMutation({
    mutationFn: (data: AdminUpdateData) => adminService.updateAdmin(user.id, data),
    onSuccess: async () => {
      // If password reset was requested, send it after successful save
      if (sendPasswordReset) {
        try {
          await adminService.sendPasswordReset(user.id);
          toast.success('User updated and password reset email sent');
        } catch (error) {
          console.error('Password reset failed:', error);
          toast.success('User updated successfully');
          toast.error('Failed to send password reset email');
        }
      } else {
        toast.success('User updated successfully');
      }
      onSuccess();
    },
    onError: (error: any) => {
      if (error.response?.errors) {
        // Handle validation errors from Rails
        const fieldErrors: Record<string, string> = {};
        error.response.errors.forEach((err: string) => {
          if (err.includes('Email')) {
            fieldErrors.email = err;
          } else if (err.includes('First name')) {
            fieldErrors.first_name = err;
          } else if (err.includes('Last name')) {
            fieldErrors.last_name = err;
          }
        });
        setErrors(fieldErrors);
      } else {
        setErrors({ general: error.message || 'Failed to update admin user' });
      }
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    // Basic validation
    const newErrors: Record<string, string> = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.email.includes('@')) newErrors.email = 'Invalid email format';
    
    // Validate affiliate selection for affiliate roles
    if ((formData.role === 'affiliate' || formData.role === 'affiliate_admin') && !formData.affiliate_id) {
      newErrors.affiliate_id = 'Please select a GAN account';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Only send changed fields
    const updateData: AdminUpdateData = {};
    if (formData.first_name !== user.first_name) updateData.first_name = formData.first_name;
    if (formData.last_name !== user.last_name) updateData.last_name = formData.last_name;
    if (formData.email !== user.email) updateData.email = formData.email;
    if (formData.role !== user.role) updateData.role = formData.role;
    if (formData.status !== user.status) updateData.status = formData.status;
    
    // Handle affiliate_id changes
    if (formData.role === 'affiliate' || formData.role === 'affiliate_admin') {
      if (formData.affiliate_id !== (user.affiliate_id || '')) {
        updateData.affiliate_id = formData.affiliate_id;
      }
    } else if (user.affiliate_id) {
      // Clear affiliate_id if role changed to non-affiliate
      updateData.affiliate_id = null as any;
    }

    // If no fields changed and no password reset requested, just close the dialog
    if (Object.keys(updateData).length === 0 && !sendPasswordReset) {
      onClose();
      return;
    }

    // If only password reset is requested with no other changes, send it directly
    if (Object.keys(updateData).length === 0 && sendPasswordReset) {
      try {
        await adminService.sendPasswordReset(user.id);
        toast.success('Password reset email sent');
        onSuccess();
      } catch (error) {
        console.error('Password reset failed:', error);
        toast.error('Failed to send password reset email');
      }
      return;
    }

    updateMutation.mutate(updateData);
  };

  // Available roles based on permissions
  const availableRoles = canEditSuper
    ? [
        { value: 'super', label: 'Super Admin' },
        { value: 'admin', label: 'Administrative User' },
        { value: 'data', label: 'Customer Editor' },
        { value: 'affiliate', label: 'Affiliate' },
        { value: 'affiliate_admin', label: 'Affiliate + Admin' },
      ]
    : [
        { value: 'admin', label: 'Administrative User' },
        { value: 'data', label: 'Customer Editor' },
        { value: 'affiliate', label: 'Affiliate' },
        { value: 'affiliate_admin', label: 'Affiliate + Admin' },
      ];

  // Don't allow non-super admins to edit super admins
  const canEditThisUser = canEditSuper || !user.is_super;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Admin User</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {errors.general && (
            <Alert variant="destructive">
              <AlertCircle className="h-5 w-5" />
              <AlertDescription>{errors.general}</AlertDescription>
            </Alert>
          )}

          {!canEditThisUser && (
            <Alert variant="warning">
              <AlertCircle className="h-5 w-5" />
              <AlertDescription>You do not have permission to edit super admin users.</AlertDescription>
            </Alert>
          )}

          {formData.email.endsWith('@prosci.com') && (
            <Alert variant="neutral">
              <Info className="h-5 w-5" />
              <AlertDescription>Internal Prosci users can use SSO to login.</AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
            <Field>
              <Label htmlFor="first_name" required>
                First Name
              </Label>
              <Input
                type="text"
                id="first_name"
                required
                disabled={!canEditThisUser}
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className={errors.first_name ? 'aria-invalid:border-destructive' : ''}
                aria-invalid={!!errors.first_name}
              />
              {errors.first_name && (
                <FieldError>{errors.first_name}</FieldError>
              )}
            </Field>

            <Field>
              <Label htmlFor="last_name" required>
                Last Name
              </Label>
              <Input
                type="text"
                id="last_name"
                required
                disabled={!canEditThisUser}
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className={errors.last_name ? 'aria-invalid:border-destructive' : ''}
                aria-invalid={!!errors.last_name}
              />
              {errors.last_name && (
                <FieldError>{errors.last_name}</FieldError>
              )}
            </Field>
          </div>

          <Field>
            <Label htmlFor="email" required>
              Email Address
            </Label>
            <Input
              type="email"
              id="email"
              required
              disabled={!canEditThisUser}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value.toLowerCase() })}
              className={errors.email ? 'aria-invalid:border-destructive' : ''}
              aria-invalid={!!errors.email}
            />
            {errors.email && (
              <FieldError>{errors.email}</FieldError>
            )}
          </Field>

          <Field>
            <Label htmlFor="role" required>
              Access Level
            </Label>
            <Select 
              value={formData.role} 
              onValueChange={(value) => setFormData({ ...formData, role: value as AdminUser['role'] })}
              disabled={!canEditThisUser || (user.is_super && !canEditSuper)}
            >
              <SelectTrigger id="role" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableRoles.map(role => (
                  <SelectItem key={role.value} value={role.value}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-sm text-muted-foreground">
              {formData.role === 'super' && 'Full system access with ability to manage other super admins'}
              {formData.role === 'admin' && 'Can manage users and content, but not super admins'}
              {formData.role === 'data' && 'Can edit customer data and portal content'}
              {formData.role === 'affiliate' && 'Limited access for affiliate partners'}
              {formData.role === 'affiliate_admin' && 'Affiliate access with administrative privileges'}
            </p>
          </Field>

          {(formData.role === 'affiliate' || formData.role === 'affiliate_admin') && (
            <Field>
              <Label htmlFor="affiliate_id" required>
                GAN Account
              </Label>
              <Select 
                value={formData.affiliate_id} 
                onValueChange={(value) => setFormData({ ...formData, affiliate_id: value })}
                disabled={!canEditThisUser}
                required
              >
                <SelectTrigger id="affiliate_id" className={`w-full ${errors.affiliate_id ? 'border-destructive' : ''}`}>
                  <SelectValue placeholder="Select a GAN account..." />
                </SelectTrigger>
                <SelectContent>
                  {affiliates.map(affiliate => (
                    <SelectItem key={affiliate.id} value={affiliate.id}>
                      {affiliate.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.affiliate_id && (
                <FieldError>{errors.affiliate_id}</FieldError>
              )}
              {user.affiliate_name && formData.affiliate_id === user.affiliate_id && (
                <p className="mt-1 text-sm text-muted-foreground">
                  Current: {user.affiliate_name}
                </p>
              )}
            </Field>
          )}

          <div>
            <div className="flex items-center gap-4">
              <Switch
                id="active-status"
                checked={formData.status === 'active'}
                onCheckedChange={(checked) => setFormData({ ...formData, status: checked ? 'active' : 'inactive' })}
                disabled={!canEditThisUser}
                showLabels
              />
              <div>
                <Label htmlFor="active-status">Active Status</Label>
                <p className="mt-1 text-sm text-muted-foreground">
                  {formData.status === 'active'
                    ? 'User can log in to the admin portal'
                    : 'User will not be able to log in to the admin portal'}
                </p>
              </div>
            </div>
          </div>

          {/* Password reset only available for non-SSO users (not @prosci.com) */}
          {canEditThisUser && !formData.email.endsWith('@prosci.com') && (
            <div className="border-t pt-4">
              <Label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={sendPasswordReset}
                  onCheckedChange={(checked) => setSendPasswordReset(!!checked)}
                />
                <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <Key className="h-4 w-4" />
                  Send password reset email after saving
                </span>
              </Label>
              <p className="mt-1 ml-7 text-sm text-muted-foreground">
                The user will receive an email with instructions to reset their password
              </p>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={updateMutation.isPending || !canEditThisUser}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}